import { DefaultLoadingManager, LoaderUtils, LoadingManager, SRGBColorSpace, RGBAFormat } from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js";
import { RethrownError } from "../utils/errors";
import { proxiedUrlFor } from "../../ui/utils/media-url-utils";
import { getComponents, HUBS_NODEREF_COMPONENTS } from "./moz-hubs-components";

/**
 * Adapter class that wraps Three.js GLTFLoader to provide compatibility
 * with the old GLTFLoader API.
 *
 * Maintains the API: new GLTFLoaderAdapter(url, manager, options).loadGLTF()
 */
export class GLTFLoaderAdapter {
  constructor(url, manager, options = {}) {
    this.url = url;
    this.manager = manager !== undefined ? manager : DefaultLoadingManager;
    this.options = {
      revokeObjectURLs: true,
      crossOrigin: true,
      addUnknownExtensionsToUserData: false,
      path: undefined,
      ...options
    };

    // Initialize stats structure matching old GLTFLoader
    this.stats = {
      nodes: 0,
      meshes: 0,
      materials: 0,
      textures: 0,
      triangles: 0,
      vertices: 0,
      totalSize: 0,
      jsonSize: 0,
      bufferInfo: {},
      textureInfo: {},
      meshInfo: {}
    };

    // Create the new GLTFLoader instance
    this.loader = new GLTFLoader(this.manager);

    // Set path if provided, but only for relative URLs
    // For absolute URLs (http, https, blob, data, /), don't set a path to avoid duplication
    if (this.options.path !== undefined) {
      this.loader.setPath(this.options.path);
    } else {
      // Only extract base URL if it's not already an absolute URL or absolute path
      // Absolute URLs start with http://, https://, blob:, data:, //, or /
      const isAbsoluteUrl = /^(https?:|blob:|data:|\/)/i.test(url);
      if (!isAbsoluteUrl) {
        this.loader.setPath(LoaderUtils.extractUrlBase(url));
      } else {
        // For absolute URLs, set empty path to prevent URL duplication
        this.loader.setPath("");
      }
    }

    // Set cross origin
    if (this.options.crossOrigin) {
      this.loader.setCrossOrigin(this.options.crossOrigin === "use-credentials" ? "use-credentials" : "anonymous");
    }

    // Configure DRACOLoader by default if not explicitly disabled or provided
    if (this.options.dracoLoader !== null && this.options.dracoLoader !== false) {
      if (this.options.dracoLoader) {
        // Use provided DRACOLoader instance
        this.setDRACOLoader(this.options.dracoLoader);
      } else {
        // Create and configure default DRACOLoader
        const dracoLoader = new DRACOLoader();
        const decoderPath = "https://www.gstatic.com/draco/versioned/decoders/1.5.7/";
        dracoLoader.setDecoderPath(proxiedUrlFor(decoderPath));
        dracoLoader.setDecoderConfig({ type: "js" });
        this.setDRACOLoader(dracoLoader);
      }
    }

    // Cache for dependencies (for getDependency compatibility)
    this.cache = new Map();
    this.gltfResult = null;
  }

  /**
   * Loads the glTF file and returns scene, json, and stats.
   * Maintains compatibility with old GLTFLoader API.
   *
   * @returns {Promise<{scene: Group, json: Object, stats: Object}>}
   */
  async loadGLTF() {
    try {
      // Ensure path is empty for absolute URLs to prevent duplication
      // Check for http://, https://, blob:, data:, //, or / URLs
      const isAbsoluteUrl = /^(https?:|blob:|data:|\/)/i.test(this.url);
      if (isAbsoluteUrl && this.loader.path !== "") {
        this.loader.setPath("");
      }

      // Use loadAsync from the base Loader class, or wrap load() if not available
      let gltf;

      // Hack: Disable createImageBitmap temporarily if revokeObjectURLs is false.
      // This forces GLTFLoader to use HTMLImageElement instead of ImageBitmap,
      // ensuring that textures have a .src property (blob URL) which is required
      // by GLTFExporter and other tools that need to read the texture source.
      const originalCreateImageBitmap = window.createImageBitmap;
      if (this.options.revokeObjectURLs === false) {
        window.createImageBitmap = undefined;
      }

      try {
        if (typeof this.loader.loadAsync === "function") {
          gltf = await this.loader.loadAsync(this.url);
        } else {
          // Fallback: wrap load() method in a promise
          gltf = await new Promise((resolve, reject) => {
            this.loader.load(
              this.url,
              resolve,
              undefined, // onProgress
              reject // onError
            );
          });
        }
      } finally {
        // Restore createImageBitmap
        if (this.options.revokeObjectURLs === false) {
          window.createImageBitmap = originalCreateImageBitmap;
        }
      }

      // Store for getDependency access
      this.gltfResult = gltf;

      // Calculate stats from the loaded glTF
      this.calculateStats(gltf);

      // Load scene-specific animations (matching old GLTFLoader behavior)
      const json = gltf.parser.json;
      const sceneIndex = json.scene || 0;
      const sceneAnimations = await this.loadSceneAnimations(sceneIndex);

      // Fix animation track names to use UUIDs instead of node names
      // This ensures compatibility with ModelNode.updateStaticModes which expects UUIDs
      this.fixAnimationTrackNames(gltf.scene, sceneAnimations);

      // Assign animations to the scene (matching old GLTFLoader behavior)
      gltf.scene.animations = sceneAnimations || [];

      // Post-process MOZ_hubs_components to resolve node references
      await this.processHubsComponents(gltf);

      // Post-process MOZ_lightmap extension to apply lightmaps to materials
      await this.processLightmaps(gltf);

      return {
        scene: gltf.scene,
        json: gltf.parser.json,
        stats: this.stats
      };
    } catch (error) {
      throw new RethrownError(`Error loading glTF "${this.url}"`, error);
    }
  }

  /**
   * Calculates statistics from the loaded glTF.
   * Matches the stats structure from the old GLTFLoader.
   *
   * @param {Object} gltf - The loaded glTF object from Three.js GLTFLoader
   */
  calculateStats(gltf) {
    const json = gltf.parser.json;

    // Calculate JSON size
    const jsonString = JSON.stringify(json);
    this.stats.jsonSize = new Blob([jsonString]).size;
    this.stats.totalSize = this.stats.jsonSize;

    // Count nodes, meshes, materials, textures
    this.stats.nodes = (json.nodes || []).length;
    this.stats.meshes = (json.meshes || []).length;
    this.stats.materials = (json.materials || []).length;
    this.stats.textures = (json.textures || []).length;

    // Calculate buffer sizes
    if (json.buffers) {
      for (let i = 0; i < json.buffers.length; i++) {
        const bufferDef = json.buffers[i];

        if (bufferDef.uri) {
          // External buffer - size will be calculated when loaded
          this.stats.bufferInfo[i] = {
            uri: bufferDef.uri,
            size: undefined
          };
        } else if (i === 0 && gltf.parser.extensions?.["KHR_binary_glTF"]) {
          // GLB binary chunk
          const binaryExtension = gltf.parser.extensions["KHR_binary_glTF"];
          if (binaryExtension.body) {
            const size = binaryExtension.body.byteLength;
            this.stats.bufferInfo[i] = {
              size: size
            };
            this.stats.totalSize += size;
          }
        }
      }
    }

    // Calculate texture sizes (approximate)
    if (json.images) {
      for (let i = 0; i < json.images.length; i++) {
        const imageDef = json.images[i];
        if (imageDef) {
          const uri =
            imageDef.uri || (imageDef.bufferView !== undefined ? `bufferView:${imageDef.bufferView}` : `image_${i}`);
          this.stats.textureInfo[i] = {
            uri: uri,
            size: undefined // Will be filled when texture is loaded
          };
        }
      }
    }

    // Count vertices and triangles from meshes
    let totalVertices = 0;
    let totalTriangles = 0;

    if (json.meshes) {
      for (let i = 0; i < json.meshes.length; i++) {
        const meshDef = json.meshes[i];
        let meshVertices = 0;
        let meshTriangles = 0;

        for (const primitive of meshDef.primitives || []) {
          // Get vertex count from POSITION accessor
          if (primitive.attributes?.POSITION !== undefined) {
            const accessorIndex = primitive.attributes.POSITION;
            const accessor = json.accessors?.[accessorIndex];
            if (accessor) {
              meshVertices += accessor.count;
            }
          }

          // Get triangle count from indices
          if (primitive.indices !== undefined) {
            const accessor = json.accessors?.[primitive.indices];
            if (accessor) {
              // For triangles, divide by 3
              meshTriangles += Math.floor(accessor.count / 3);
            } else if (primitive.attributes?.POSITION !== undefined) {
              // No indices, assume triangles from vertices
              const accessor = json.accessors?.[primitive.attributes.POSITION];
              if (accessor) {
                meshTriangles += Math.floor(accessor.count / 3);
              }
            }
          } else if (primitive.attributes?.POSITION !== undefined) {
            // No indices, assume triangles from vertices
            const accessor = json.accessors?.[primitive.attributes.POSITION];
            if (accessor) {
              meshTriangles += Math.floor(accessor.count / 3);
            }
          }
        }

        this.stats.meshInfo[i] = {
          vertices: meshVertices,
          triangles: meshTriangles
        };

        totalVertices += meshVertices;
        totalTriangles += meshTriangles;
      }
    }

    this.stats.vertices = totalVertices;
    this.stats.triangles = totalTriangles;
  }

  /**
   * Creates a LoadingManager with proxied URL resolution for DRACOLoader.
   * This ensures draco decoder files and other resources are fetched through the proxy.
   *
   * @returns {LoadingManager} A LoadingManager configured with proxied URL resolution
   */
  _createProxiedLoadingManager() {
    const manager = new LoadingManager();
    const originalResolveURL = manager.resolveURL || (url => url);

    // Override resolveURL to use proxied URLs
    manager.resolveURL = url => {
      if (typeof url !== "string" || url === "") return url;

      // Skip proxying for data URLs, blob URLs, and relative paths
      if (/^(data:|blob:)/i.test(url)) return url;
      if (!/^(https?:|\/\/)/i.test(url)) return originalResolveURL(url);

      // Use proxied URL for HTTP/HTTPS URLs
      return proxiedUrlFor(url);
    };

    return manager;
  }

  /**
   * Sets the given Draco loader to this loader. Required for decoding assets
   * compressed with the `KHR_draco_mesh_compression` extension.
   * Configures the draco loader to use proxied URLs for fetching resources.
   *
   * @param {DRACOLoader} dracoLoader - The Draco loader to set.
   * @return {GLTFLoaderAdapter} A reference to this loader.
   */
  setDRACOLoader(dracoLoader) {
    if (dracoLoader) {
      // Configure draco loader to use proxied URLs by setting a custom LoadingManager
      if (dracoLoader.manager) {
        const proxiedManager = this._createProxiedLoadingManager();
        dracoLoader.manager = proxiedManager;
      }

      // Override resolveURL method if it exists to use proxied URLs
      if (dracoLoader.resolveURL) {
        const originalResolveURL = dracoLoader.resolveURL.bind(dracoLoader);
        dracoLoader.resolveURL = url => {
          if (typeof url !== "string" || url === "") return url;
          // Skip proxying for data URLs, blob URLs, and relative paths
          if (/^(data:|blob:)/i.test(url)) return url;
          if (!/^(https?:|\/\/)/i.test(url)) return originalResolveURL(url);
          // Use proxied URL for HTTP/HTTPS URLs
          return proxiedUrlFor(url);
        };
      }
    }

    this.loader.setDRACOLoader(dracoLoader);
    return this;
  }

  /**
   * Gets a dependency from the loaded glTF.
   * Maintains compatibility with old GLTFLoader API for ModelNode.
   *
   * @param {string} type - The type of dependency (e.g., "gltf", "scene", "node", etc.)
   * @param {number} [index] - Optional index for the dependency
   * @param {Object} [_options] - Optional options (unused but kept for API compatibility)
   * @returns {Promise<any>}
   */
  async getDependency(type, index, _options = {}) {
    if (!this.gltfResult) {
      // If not loaded yet, load it first
      await this.loadGLTF();
    }

    const gltf = this.gltfResult;
    const parser = gltf.parser;
    const json = parser.json;

    // Handle special "gltf" type that ModelNode uses
    if (type === "gltf") {
      return {
        scene: gltf.scene,
        json: json,
        stats: this.stats
      };
    }

    // Handle "root" type
    if (type === "root") {
      return { json };
    }

    // Handle "scene" type
    if (type === "scene") {
      const sceneIndex = index !== undefined ? index : json.scene || 0;
      const scenes = gltf.scenes || [];
      if (scenes[sceneIndex]) {
        return scenes[sceneIndex];
      }
      // Fallback to default scene
      return gltf.scene;
    }

    // Handle "sceneAnimations" type - filter animations for the specific scene
    if (type === "sceneAnimations") {
      const sceneIndex = index !== undefined ? index : json.scene || 0;
      return this.loadSceneAnimations(sceneIndex);
    }

    // Delegate to parser's getDependency for other types
    if (parser.getDependency) {
      return parser.getDependency(type, index);
    }

    throw new Error(`Unknown dependency type: ${type}`);
  }

  /**
   * Loads animations for a specific scene by filtering animations that target nodes in the scene.
   * Matches the behavior of the old GLTFLoader.
   *
   * @param {number} sceneIndex - The scene index
   * @returns {Promise<Array>} Array of AnimationClip objects for this scene
   */
  async loadSceneAnimations(sceneIndex) {
    const json = this.gltfResult.parser.json;
    const sceneDef = json.scenes[sceneIndex];

    const descendantNodeIds = [];

    if (Array.isArray(sceneDef.nodes)) {
      for (const nodeId of sceneDef.nodes) {
        this.gatherDescendantNodeIds(json, nodeId, descendantNodeIds);
      }
    }

    return this.loadAnimationsForNodes(json, descendantNodeIds);
  }

  /**
   * Recursively gathers all descendant node IDs starting from a given node.
   * Matches the behavior of the old GLTFLoader.
   *
   * @param {Object} json - The glTF JSON
   * @param {number} nodeIndex - The starting node index
   * @param {Array} descendantNodeIds - Array to accumulate node IDs
   * @returns {Array} The accumulated array of descendant node IDs
   */
  gatherDescendantNodeIds(json, nodeIndex, descendantNodeIds) {
    const nodeDef = json.nodes[nodeIndex];

    descendantNodeIds.push(nodeIndex);

    if (nodeDef.children) {
      for (const childIndex of nodeDef.children) {
        this.gatherDescendantNodeIds(json, childIndex, descendantNodeIds);
      }
    }

    return descendantNodeIds;
  }

  /**
   * Loads animations that target specific nodes.
   * Matches the behavior of the old GLTFLoader.
   *
   * @param {Object} json - The glTF JSON
   * @param {Array} nodeIds - Array of node IDs to filter animations by
   * @returns {Promise<Array>} Array of AnimationClip objects
   */
  async loadAnimationsForNodes(json, nodeIds) {
    const animationDefs = json.animations || [];
    const parser = this.gltfResult.parser;

    const pending = [];

    for (let animationIndex = 0; animationIndex < animationDefs.length; animationIndex++) {
      const animationDef = animationDefs[animationIndex];
      const animationTargetsDescendant = animationDef.channels.some(channel => {
        // Skip channels without a target node (matching Three.js GLTFLoader behavior)
        if (channel.target.node === undefined) return false;
        return nodeIds.indexOf(channel.target.node) !== -1;
      });

      if (animationTargetsDescendant) {
        // Load animation using the parser's getDependency method
        pending.push(parser.getDependency("animation", animationIndex));
      }
    }

    const animations = await Promise.all(pending);

    // Filter out any undefined/null animations and validate tracks
    return animations.filter(animation => {
      if (!animation || !animation.tracks || !Array.isArray(animation.tracks)) {
        return false;
      }

      // Ensure all tracks are valid
      return animation.tracks.every(track => track && track.name);
    });
  }

  /**
   * Fixes animation track names to use UUIDs instead of node names.
   * The Three.js GLTFLoader uses node names in track names (e.g., "nodeName.position"),
   * but the old GLTFLoader used UUIDs (e.g., "uuid.position").
   * ModelNode.updateStaticModes expects UUIDs, so we need to convert them.
   *
   * @param {Object3D} scene - The loaded scene
   * @param {Array<AnimationClip>} animations - Array of animation clips
   */
  fixAnimationTrackNames(scene, animations) {
    if (!animations || animations.length === 0) return;

    // Build a map of node names to UUIDs
    const nameToUuidMap = new Map();
    scene.traverse(node => {
      if (node.name) {
        nameToUuidMap.set(node.name, node.uuid);
      }
    });

    // Fix each animation's track names
    for (const animation of animations) {
      if (!animation.tracks) continue;

      for (const track of animation.tracks) {
        if (!track.name) continue;

        // Parse the track name: "nodeName.property"
        const dotIndex = track.name.lastIndexOf(".");
        if (dotIndex === -1) continue;

        const nodeName = track.name.substring(0, dotIndex);
        const property = track.name.substring(dotIndex + 1);

        // Look up the UUID for this node name
        const uuid = nameToUuidMap.get(nodeName);
        if (uuid) {
          // Replace the track name with UUID-based name
          track.name = uuid + "." + property;
        }
      }
    }
  }

  /**
   * Post-processes MOZ_hubs_components to resolve node references.
   * This ensures components with node references work correctly by converting
   * node indices to UUIDs, matching the behavior of the old GLTFLoader.
   *
   * @param {Object} gltf - The loaded glTF result
   */
  async processHubsComponents(gltf) {
    const scene = gltf.scene;
    if (!scene) return;

    // Traverse the scene to find all objects with MOZ_hubs_components
    const objectsWithComponents = [];
    scene.traverse(object => {
      const components = getComponents(object);
      if (components) {
        objectsWithComponents.push({ components });
      }
    });

    // Process each object's components
    const nodeResolutionPromises = [];

    for (const { components } of objectsWithComponents) {
      for (const [componentName, componentProps] of Object.entries(components)) {
        if (!componentProps || typeof componentProps !== "object") continue;

        for (const [propName, propValue] of Object.entries(componentProps)) {
          if (propValue === null || propValue === undefined) continue;

          // Handle typed references (__mhc_link_type)
          const type = propValue && propValue.__mhc_link_type;
          if (type && propValue.index !== undefined) {
            if (type === "node") {
              // Resolve node reference
              const promise = this.gltfResult.parser
                .getDependency(type, propValue.index)
                .then(node => {
                  if (node) {
                    node.userData.MOZ_spoke_uuid = node.uuid;
                    componentProps[propName] = {
                      __gltfIndexForUUID: node.uuid
                    };
                  }
                })
                .catch(error => {
                  console.warn(`Failed to resolve node reference for component ${componentName}.${propName}:`, error);
                });
              nodeResolutionPromises.push(promise);
            } else {
              console.warn(
                `Spoke currently only supports "node" component references. ` +
                  `This component ${componentName} is using a ${type} ref for ${propName}`
              );
            }
          }
          // Handle legacy node references (HUBS_NODEREF_COMPONENTS)
          else if (
            HUBS_NODEREF_COMPONENTS[componentName] &&
            HUBS_NODEREF_COMPONENTS[componentName].indexOf(propName) !== -1 &&
            typeof propValue === "number"
          ) {
            // Legacy format: propValue is directly the node index
            const promise = this.gltfResult.parser
              .getDependency("node", propValue)
              .then(node => {
                if (node) {
                  node.userData.MOZ_spoke_uuid = node.uuid;
                  componentProps[propName] = { __gltfIndexForUUID: node.uuid };
                }
              })
              .catch(error => {
                console.warn(
                  `Failed to resolve legacy node reference for component ${componentName}.${propName}:`,
                  error
                );
              });
            nodeResolutionPromises.push(promise);
          }
        }
      }
    }

    // Wait for all node references to be resolved
    // Note: This is async but we don't block on it in practice since components
    // are typically only used at export time, well after this Promise resolves.
    await Promise.all(nodeResolutionPromises);
  }

  /**
   * Post-processes MOZ_lightmap extension to apply lightmap textures to materials.
   * This ensures materials with MOZ_lightmap extension get lightmaps applied correctly.
   *
   * @param {Object} gltf - The loaded glTF result
   */
  async processLightmaps(gltf) {
    const scene = gltf.scene;
    const parser = gltf.parser;
    const json = parser.json;

    if (!scene || !json.materials) return;

    // Create a map of material index to material object for quick lookup
    const materialMap = new Map();
    scene.traverse(object => {
      if (object.material) {
        // Handle both single materials and arrays
        const materials = Array.isArray(object.material) ? object.material : [object.material];
        materials.forEach(material => {
          // Get material index from associations if available
          const gltfReference = parser.associations?.get(material);
          if (gltfReference && gltfReference.materials !== undefined) {
            materialMap.set(gltfReference.materials, material);
          }
        });
      }
    });

    // Process each material definition that has MOZ_lightmap extension
    const lightmapPromises = [];

    for (let materialIndex = 0; materialIndex < json.materials.length; materialIndex++) {
      const materialDef = json.materials[materialIndex];
      if (!materialDef.extensions) continue;

      const lightmapExtension = materialDef.extensions.MOZ_lightmap;
      if (!lightmapExtension) continue;

      // Find the corresponding Three.js material
      const material = materialMap.get(materialIndex);
      if (!material) {
        // If not found in map, try to find it by traversing again
        // This handles cases where associations might not be set
        scene.traverse(object => {
          if (object.material) {
            const materials = Array.isArray(object.material) ? object.material : [object.material];
            materials.forEach(mat => {
              const ref = parser.associations?.get(mat);
              if (ref && ref.materials === materialIndex && !materialMap.has(materialIndex)) {
                materialMap.set(materialIndex, mat);
              }
            });
          }
        });
      }

      const targetMaterial = materialMap.get(materialIndex);
      if (!targetMaterial) {
        console.warn(`Could not find material object for material index ${materialIndex} with MOZ_lightmap extension`);
        continue;
      }

      // Process the lightmap
      const promise = this.applyLightmap(targetMaterial, lightmapExtension, parser).catch(error => {
        console.warn(`Failed to apply lightmap to material ${materialIndex}:`, error);
      });

      lightmapPromises.push(promise);
    }

    // Wait for all lightmaps to be applied
    await Promise.all(lightmapPromises);
  }

  /**
   * Applies a lightmap texture to a material.
   *
   * @param {Material} material - The Three.js material to apply lightmap to
   * @param {Object} lightmapDef - The lightmap extension definition from glTF
   * @param {GLTFParser} parser - The GLTF parser instance
   */
  async applyLightmap(material, lightmapDef, parser) {
    if (!lightmapDef || lightmapDef.index === undefined) {
      return;
    }

    // Load the texture using the parser
    const texture = await parser.getDependency("texture", lightmapDef.index);

    if (!texture) {
      console.warn("Failed to load lightmap texture");
      return;
    }

    // Clone texture if needed to avoid affecting other uses
    let lightmapTexture = texture;
    if (texture.userData && texture.userData.referenceCount > 1) {
      lightmapTexture = texture.clone();
    }

    // Set color space and format
    lightmapTexture.colorSpace = SRGBColorSpace;
    if (!lightmapTexture.isCompressedTexture) {
      lightmapTexture.format = RGBAFormat;
    }

    // Apply texture transform if present
    if (lightmapDef.extensions && lightmapDef.extensions.KHR_texture_transform) {
      const transform = lightmapDef.extensions.KHR_texture_transform;
      if (transform.offset !== undefined) {
        lightmapTexture.offset.fromArray(transform.offset);
        lightmapTexture.needsUpdate = true;
      }
      if (transform.rotation !== undefined) {
        lightmapTexture.rotation = transform.rotation;
        lightmapTexture.needsUpdate = true;
      }
      if (transform.scale !== undefined) {
        lightmapTexture.repeat.fromArray(transform.scale);
        lightmapTexture.needsUpdate = true;
      }
    }

    // Apply to material
    material.lightMap = lightmapTexture;
    material.lightMapIntensity = lightmapDef.intensity !== undefined ? lightmapDef.intensity : 1.0;
    material.needsUpdate = true;
  }
}

// Export as default for compatibility
export default GLTFLoaderAdapter;
